import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Users,
  Settings,
  FileText,
  Edit,
  Calendar,
  ChevronRight,
  FileOutput,
  Cog,
  FileStack
} from 'lucide-react';
import { dashboardService } from '../../services/dashboardService';
import Logo from '../common/Logo';

const Sidebar = ({ currentView, collapsed = false, onCloseMobile }) => {
  const navigate = useNavigate();
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      const [dashboardStats] = await Promise.all([
        dashboardService.getStatistics().catch(() => null),
      ]);

      setStatistics({
        ...dashboardStats,
      });
    } catch (error) {
      console.error('Failed to load sidebar statistics:', error);
      setStatistics(null);
    } finally {
      setLoading(false);
    }
  };

  const getBadgeCount = (itemId) => {
    if (!statistics) return null;

    switch (itemId) {
      case 'contracts':
        // Show pending contracts (Sent + Accessed)
        return statistics.pendingContracts > 0 ? statistics.pendingContracts : null;
      default:
        return null;
    }
  };

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: BarChart3,
      route: '/dashboard',
      badge: null
    },
    {
      id: 'clients',
      label: 'Clienți',
      icon: Users,
      route: '/clients',
      badge: null
    },
    {
      id: 'services',
      label: 'Servicii',
      icon: Settings,
      route: '/services',
      badge: null
    },
    {
      id: 'contracts',
      label: 'Contracte',
      icon: FileText,
      route: '/contracts',
      badge: getBadgeCount('contracts')
    },
    {
      id: 'templates',
      label: 'Template-uri',
      icon: Edit,
      route: '/templates',
      badge: null
    },
    {
      id: 'bulk-contracts',
      label: 'Contracte în Bulk',
      icon: FileStack,
      route: '/bulk-contracts',
      badge: null
    },
    {
      id: 'standalone-acts',
      label: 'Acte Adiționale Standalone',
      icon: FileOutput,
      route: '/standalone-acts',
      badge: null
    },
    {
      id: 'settings',
      label: 'Setări',
      icon: Cog,
      route: '/settings',
      badge: null
    }
  ];

  const handleNavigation = (item) => {
    navigate(item.route);
    // Close mobile sidebar when navigating
    if (onCloseMobile) {
      onCloseMobile();
    }
  };

  return (
    <aside className={`bg-white shadow-sm border-r border-gray-200 transition-all duration-300 h-full ${collapsed ? 'w-16' : 'w-64'
      }`}>
      <div className="p-4 h-full flex flex-col">
        {/* Logo/Brand */}
        <div className={`p-4 border-b border-gray-200 ${collapsed ? 'flex justify-center' : ''}`}>
          <div className={`p-4 border-b border-gray-200 flex justify-center`}>
            <Logo size={collapsed ? 'md' : 'lg'} className="flex-shrink-0" />
          </div>
        </div>

        {/* Navigation */}
        <nav className="space-y-2 flex-1">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleNavigation(item)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-left transition-all duration-200 group ${currentView === item.id
                ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              title={collapsed ? item.label : ''}
            >
              <div className="flex items-center space-x-3">
                <item.icon className={`w-5 h-5 flex-shrink-0 ${currentView === item.id ? 'text-blue-700' : 'text-gray-500 group-hover:text-gray-700'
                  }`} />
                {!collapsed && (
                  <span className="font-medium">{item.label}</span>
                )}
              </div>

              {!collapsed && (
                <div className="flex items-center space-x-2">
                  {item.badge && (
                    <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5 min-w-[20px] text-center">
                      {loading ? '•' : item.badge}
                    </span>
                  )}
                  {currentView === item.id && (
                    <ChevronRight className="w-4 h-4 text-blue-700" />
                  )}
                </div>
              )}

              {/* Badge for collapsed sidebar */}
              {collapsed && item.badge && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {loading ? '•' : item.badge}
                </span>
              )}
            </button>
          ))}
        </nav>

        {/* Statistics Footer (only for expanded sidebar) */}
        {!collapsed && statistics && (
          <div className="mt-auto pt-4 border-t border-gray-200">
            <div className="text-xs text-gray-500 space-y-1">
              <div className="flex justify-between">
                <span>Total contracte:</span>
                <span className="font-medium">{statistics.totalContracts || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>Clienți activi:</span>
                <span className="font-medium">{statistics.totalClients || 0}</span>
              </div>
              {statistics.expiringContracts > 0 && (
                <div className="flex justify-between text-orange-600">
                  <span>Expiră curând:</span>
                  <span className="font-medium">{statistics.expiringContracts}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;